import { endOfWeek, format, getISOWeek, getISOWeekYear, setWeek, setYear, startOfWeek, getISOWeeksInYear } from 'date-fns';
import { Maybe } from '../generated/graphql';
import config from '../services/config';
import { parseDateGQLField } from '../services/utils';
export { getCurrentIsoYear } from '../services/utils';

interface WeekWithYear {
    year: number;
    week: number;
}

export function adjustDateForTimezoneOffset(inputDate: Date): string {
    inputDate.setHours(12);
    let dateString = format(inputDate, 'yyyy-MM-dd');
    return dateString;
}

export function startOfWeekOfWeekWithYear(weekYear: WeekWithYear): Date {
    const { week, year } = weekYear
    let date = new Date()
    date = setYear(date, year)
    date = setWeek(date, week)
    return startOfWeek(date, { weekStartsOn: 1 })
}

export function endOfWeekOfWeekWithYear(weekYear: WeekWithYear): Date {
    const { week, year } = weekYear
    let date = new Date();
    date = setYear(date, year)
    date = setWeek(date, week)
    return endOfWeek(date, { weekStartsOn: 1 })
}

export function formatDate(date: Date | null) {
    if (!date) return "-"
    try {
        return format(date, config.GIN_DATE_FORMAT)
    } catch (e: unknown) {
        return "-"
    }
}

export function getISOWeekWithYearFromDate(date: Date): WeekWithYear {
    return { week: getISOWeek(date), year: getISOWeekYear(date) }
}

export function getISOWeekWithYearFromDateString(dateStr: string): Maybe<WeekWithYear> {
    const date = parseDateGQLField(dateStr)
    if (!date) return null
    return getISOWeekWithYearFromDate(date)
}

function isValidWeek(year: number, week: number): boolean {
    const maxWeeks = getISOWeeksInYear(new Date(year, 0, 1));
    return week >= 1 && week <= maxWeeks;
}

/**
 * Determines whether a given week (represented by year and week number)
 * falls within a specified range of weeks, inclusive.
 *
 * Note:
 * This function uses a simplified approach by converting each week to a
 * representative Date object based on `(week - 1) * 7` days from January 1st
 * of the given year. This is not ISO-compliant and does not guarantee the
 * exact start of the week (e.g., Monday), but it is sufficient for comparing
 * the relative order of weeks.
 *
 * @param startWeek - The start of the week range (inclusive).
 * @param endWeek - The end of the week range (inclusive).
 * @param week - The week to test.
 * @returns True if the week is within the range; otherwise, false.
 */
export function isWeekWithYearInRange(startWeek: WeekWithYear, endWeek: WeekWithYear, week: WeekWithYear): boolean {
    if (
        !isValidWeek(startWeek.year, startWeek.week) ||
        !isValidWeek(endWeek.year, endWeek.week) ||
        !isValidWeek(week.year, week.week)
    ) {
        return false;
    }
    const rangeStart = new Date(startWeek.year, 0, (startWeek.week - 1) * 7);
    const rangeEnd = new Date(endWeek.year, 0, (endWeek.week - 1) * 7);
    const target = new Date(week.year, 0, (week.week - 1) * 7);
    return target >= rangeStart && target <= rangeEnd;
}
